import React, { useState, useEffect } from 'react';
import { notificationService, userService } from '../services/api';
import { Button, Card } from './ui';
import { Bell, Mail, AlertCircle, MessageSquare } from 'lucide-react';

const NotificationSettings = ({ user }) => {
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [testNotifSending, setTestNotifSending] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  const [pushSubscription, setPushSubscription] = useState(null);
  const [smsPreferences, setSmsPreferences] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [smsSaving, setSmsSaving] = useState(false);
  const [smsTestSending, setSmsTestSending] = useState(false);

  useEffect(() => {
    loadPreferences();
    loadSMSPreferences();
  }, []);

  useEffect(() => {
    const supported = Boolean(window.Notification && 'serviceWorker' in navigator && 'PushManager' in window);
    setPushSupported(supported);
    if (window.Notification) {
      setPushPermission(Notification.permission);
    }

    if (supported) {
      navigator.serviceWorker.ready
        .then((registration) => registration.pushManager.getSubscription())
        .then((subscription) => {
          setPushSubscription(subscription);
        })
        .catch(() => {
          setPushSubscription(null);
        });
    }
  }, []);

  const loadPreferences = async () => {
    setLoading(true);
    setMessage(null);

    try {
      const response = await userService.getNotificationPreferences();
      setPreferences(response.data.data.preferences);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to load notification preferences'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadSMSPreferences = async () => {
    try {
      const response = await notificationService.getSMSPreferences();
      setSmsPreferences(response.data.data.smsNotifications);
      setPhoneNumber(response.data.data.phone || '');
    } catch (error) {
      console.error('Failed to load SMS preferences:', error);
    }
  };

  const handleFrequencyChange = (value) => {
    setPreferences(prev => ({
      ...prev,
      frequency: value
    }));
  };

  const handleEmailTimeChange = (value) => {
    setPreferences(prev => ({
      ...prev,
      emailNotifications: {
        ...prev.emailNotifications,
        preferredTime: value
      }
    }));
  };

  const handleEmailToggle = (typeKey) => {
    setPreferences(prev => ({
      ...prev,
      emailNotifications: {
        ...prev.emailNotifications,
        types: {
          ...prev.emailNotifications.types,
          [typeKey]: !prev.emailNotifications.types[typeKey]
        }
      }
    }));
  };

  const handleEmailEnabledToggle = () => {
    setPreferences(prev => ({
      ...prev,
      emailNotifications: {
        ...prev.emailNotifications,
        enabled: !prev.emailNotifications.enabled
      }
    }));
  };

  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const ensurePushSubscription = async () => {
    const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
    if (!vapidKey) {
      throw new Error('VAPID public key missing');
    }

    const registration = await navigator.serviceWorker.ready;
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        appServerKey: urlBase64ToUint8Array(vapidKey)
      });
    }

    await notificationService.subscribePush(subscription);
    setPushSubscription(subscription);
  };

  const persistPushPreference = async (pushNotifications) => {
    const response = await userService.updateNotificationPreferences({ pushNotifications });
    setPreferences(response.data.data.preferences);
  };

  const handlePushEnabledToggle = async () => {
    setMessage(null);

    if (!pushSupported) {
      setMessage({ type: 'error', text: 'Push notifications are not supported in this browser.' });
      return;
    }

    if (preferences.pushNotifications?.enabled) {
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (subscription?.endpoint) {
          await notificationService.unsubscribePush(subscription.endpoint);
          await subscription.unsubscribe();
        }
        setPushSubscription(null);
        await persistPushPreference({
          ...(preferences.pushNotifications || {}),
          enabled: false
        });
      } catch (error) {
        setMessage({ type: 'error', text: 'Unable to disable push notifications.' });
      }
      return;
    }

    const permission = await Notification.requestPermission();
    setPushPermission(permission);
    if (permission !== 'granted') {
      setMessage({ type: 'error', text: 'Push permission was not granted.' });
      return;
    }

    try {
      await ensurePushSubscription();
      const current = preferences.pushNotifications || { enabled: false, types: {} };
      await persistPushPreference({
        ...current,
        enabled: true,
        types: {
          ...current.types
        }
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.message === 'VAPID public key missing'
          ? 'Push notifications are not configured yet.'
          : 'Unable to enable push notifications.'
      });
    }
  };

  const handlePushToggle = (typeKey) => {
    setPreferences(prev => {
      const current = prev.pushNotifications || { enabled: false, types: {} };
      return {
        ...prev,
        pushNotifications: {
          ...current,
          types: {
            ...current.types,
            [typeKey]: !current.types?.[typeKey]
          }
        }
      };
    });
  };

  const handleSmsEnabledToggle = () => {
    setSmsPreferences(prev => ({
      ...prev,
      enabled: !prev.enabled
    }));
  };

  const handleSmsTimeChange = (value) => {
    setSmsPreferences(prev => ({
      ...prev,
      preferredTime: value
    }));
  };

  const handleSmsToggle = (typeKey) => {
    setSmsPreferences(prev => ({
      ...prev,
      types: {
        ...prev.types,
        [typeKey]: !prev.types[typeKey]
      }
    }));
  };

  const handleSaveSMSPreferences = async () => {
    setSmsSaving(true);
    setMessage(null);

    try {
      await notificationService.updateSMSPreferences(smsPreferences);
      setMessage({
        type: 'success',
        text: 'SMS notification preferences saved successfully'
      });
      await loadSMSPreferences();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to save SMS preferences'
      });
    } finally {
      setSmsSaving(false);
    }
  };

  const handleSendSMSTest = async () => {
    setSmsTestSending(true);
    setMessage(null);

    try {
      await notificationService.sendSMSTest();
      setMessage({
        type: 'success',
        text: 'Test SMS sent! Check your phone.'
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to send test SMS'
      });
    } finally {
      setSmsTestSending(false);
    }
  };

  const handleSavePreferences = async () => {
    setSaving(true);
    setMessage(null);

    try {
      const response = await userService.updateNotificationPreferences(preferences);
      setPreferences(response.data.data.preferences);
      setMessage({
        type: 'success',
        text: 'Notification preferences saved successfully'
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to save preferences'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSendTestNotification = async () => {
    setTestNotifSending(true);
    setMessage(null);

    try {
      await userService.sendTestNotification('email');
      setMessage({
        type: 'success',
        text: 'Test notification sent! Check your email.'
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to send test notification'
      });
    } finally {
      setTestNotifSending(false);
    }
  };

  const handleSendPushTest = async () => {
    setTestNotifSending(true);
    setMessage(null);

    try {
      await notificationService.sendPushTest();
      setMessage({
        type: 'success',
        text: 'Test push sent! Check your device notifications.'
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to send push notification'
      });
    } finally {
      setTestNotifSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-neutral-600">Loading notification settings...</div>
      </div>
    );
  }

  if (!preferences) {
    return (
      <div className="text-center py-8 text-neutral-600">
        Unable to load notification preferences
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {message && (
        <div
          className={`rounded-soft border p-3 text-sm ${
            message.type === 'success'
              ? 'border-success-200 bg-success-50 text-success-700'
              : 'border-error-200 bg-error-50 text-error-700'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Notification Frequency */}
      <Card>
        <div className="flex items-start gap-3 mb-4">
          <Bell className="w-5 h-5 text-primary-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-neutral-900">Notification Frequency</h4>
            <p className="text-sm text-neutral-600 mt-1">How often do you want to receive notifications?</p>
          </div>
        </div>

        <div className="space-y-2 mt-4">
          {[
            { value: 'daily', label: 'Daily (Recommended)', desc: 'Full notifications every day' },
            { value: 'weekly', label: 'Weekly', desc: 'Summary once per week' },
            { value: 'important_only', label: 'Important Only', desc: 'Milestones and alerts only' },
            { value: 'none', label: 'None', desc: 'Disable all notifications' }
          ].map(option => (
            <label key={option.value} className="flex items-center p-3 rounded-soft border border-neutral-200 hover:bg-neutral-50 cursor-pointer">
              <input
                type="radio"
                name="frequency"
                value={option.value}
                checked={preferences.frequency === option.value}
                onChange={(e) => handleFrequencyChange(e.target.value)}
                className="w-4 h-4"
              />
              <div className="ml-3">
                <p className="font-medium text-neutral-900">{option.label}</p>
                <p className="text-xs text-neutral-500">{option.desc}</p>
              </div>
            </label>
          ))}
        </div>
      </Card>

      {/* Email Notifications */}
      {preferences.frequency !== 'none' && (
        <Card>
          <div className="flex items-start gap-3 mb-4">
            <Mail className="w-5 h-5 text-primary-600 mt-0.5" />
            <div>
              <h4 className="font-semibold text-neutral-900">Email Notifications</h4>
              <p className="text-sm text-neutral-600 mt-1">Receive wellness reminders and updates via email</p>
            </div>
          </div>

          <div className="mt-4 space-y-4">
            <div className="flex items-center justify-between p-3 rounded-soft border border-neutral-200 bg-neutral-50">
              <div>
                <p className="font-medium text-neutral-900">Email Notifications</p>
                <p className="text-xs text-neutral-600">Receive notifications to your email</p>
              </div>
              <button
                type="button"
                onClick={handleEmailEnabledToggle}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  preferences.emailNotifications.enabled
                    ? 'bg-primary-600'
                    : 'bg-neutral-300'
                }`}
              >
                <span
                  className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                    preferences.emailNotifications.enabled ? 'translate-x-6' : ''
                  }`}
                />
              </button>
            </div>

            {preferences.emailNotifications.enabled && (
              <>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Preferred Time for Daily Reminder
                  </label>
                  <input
                    type="time"
                    value={preferences.emailNotifications.preferredTime}
                    onChange={(e) => handleEmailTimeChange(e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-soft focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <p className="text-xs text-neutral-500 mt-1">
                    Reminder will be sent at this time in your timezone
                  </p>
                </div>

                <div>
                  <p className="text-sm font-medium text-neutral-700 mb-3">Which notifications to receive:</p>
                  <div className="space-y-2">
                    {[
                      { key: 'dailyReminder', label: 'Daily Check-in Reminder', icon: '📝' },
                      { key: 'milestones', label: 'Milestone Celebrations', icon: '🎉' },
                      { key: 'phaseTransitions', label: 'Phase Transitions', icon: '📈' },
                      { key: 'activitySuggestions', label: 'Activity Suggestions', icon: '💡' },
                      { key: 'lapseResponses', label: 'Setback Support', icon: '💪' },
                      { key: 'weeklyDigest', label: 'Weekly Progress Summary', icon: '📊' },
                      { key: 'clinicianAlerts', label: 'Clinician Messages', icon: '👨‍⚕️' }
                    ].map(notif => (
                      <label key={notif.key} className="flex items-center p-2 rounded hover:bg-neutral-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={preferences.emailNotifications.types[notif.key] || false}
                          onChange={() => handleEmailToggle(notif.key)}
                          className="w-4 h-4"
                        />
                        <span className="ml-3 text-sm text-neutral-700">
                          {notif.icon} {notif.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleSendTestNotification}
                  disabled={testNotifSending}
                  className="w-full"
                >
                  {testNotifSending ? 'Sending...' : 'Send Test Email'}
                </Button>
              </>
            )}
          </div>
        </Card>
      )}

      {preferences.frequency !== 'none' && (
        <Card>
          <div className="flex items-start gap-3 mb-4">
            <Bell className="w-5 h-5 text-primary-600 mt-0.5" />
            <div>
              <h4 className="font-semibold text-neutral-900">Push Notifications</h4>
              <p className="text-sm text-neutral-600 mt-1">
                Receive short, generic reminders on your device. No sensitive details are sent.
              </p>
            </div>
          </div>

          <div className="mt-4 space-y-4">
            <div className="flex items-center justify-between p-3 rounded-soft border border-neutral-200 bg-neutral-50">
              <div>
                <p className="font-medium text-neutral-900">Push Notifications</p>
                <p className="text-xs text-neutral-600">
                  {pushSupported
                    ? `Permission: ${pushPermission}`
                    : 'Push is not supported on this device.'}
                </p>
              </div>
              <button
                type="button"
                onClick={handlePushEnabledToggle}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  preferences.pushNotifications?.enabled
                    ? 'bg-primary-600'
                    : 'bg-neutral-300'
                }`}
              >
                <span
                  className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                    preferences.pushNotifications?.enabled ? 'translate-x-6' : ''
                  }`}
                />
              </button>
            </div>

            {preferences.pushNotifications?.enabled && (
              <>
                <div>
                  <p className="text-sm font-medium text-neutral-700 mb-3">Which push notifications to receive:</p>
                  <div className="space-y-2">
                    {[
                      { key: 'dailyReminder', label: 'Daily Check-in Reminder', icon: '📝' },
                      { key: 'milestones', label: 'Milestone Celebrations', icon: '🎉' },
                      { key: 'phaseTransitions', label: 'Phase Transitions', icon: '📈' },
                      { key: 'activitySuggestions', label: 'Activity Suggestions', icon: '💡' },
                      { key: 'lapseResponses', label: 'Setback Support', icon: '💪' },
                      { key: 'weeklyDigest', label: 'Weekly Progress Summary', icon: '📊' },
                      { key: 'clinicianAlerts', label: 'Clinician Messages', icon: '👨‍⚕️' }
                    ].map(notif => (
                      <label key={notif.key} className="flex items-center p-2 rounded hover:bg-neutral-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={preferences.pushNotifications.types?.[notif.key] || false}
                          onChange={() => handlePushToggle(notif.key)}
                          className="w-4 h-4"
                        />
                        <span className="ml-3 text-sm text-neutral-700">
                          {notif.icon} {notif.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleSendPushTest}
                  disabled={testNotifSending || !pushSubscription}
                  className="w-full"
                >
                  {testNotifSending ? 'Sending...' : 'Send Test Push'}
                </Button>
              </>
            )}
          </div>
        </Card>
      )}

      {/* SMS Notifications */}
      {preferences.frequency !== 'none' && smsPreferences && (
        <Card>
          <div className="flex items-start gap-3 mb-4">
            <MessageSquare className="w-5 h-5 text-primary-600 mt-0.5" />
            <div>
              <h4 className="font-semibold text-neutral-900">SMS Text Notifications</h4>
              <p className="text-sm text-neutral-600 mt-1">
                Receive important reminders and alerts via text message
              </p>
            </div>
          </div>

          <div className="mt-4 space-y-4">
            {!phoneNumber ? (
              <div className="p-4 rounded-soft border-2 border-dashed border-neutral-300 bg-neutral-50 text-center">
                <MessageSquare className="w-8 h-8 text-neutral-400 mx-auto mb-2" />
                <p className="text-sm font-medium text-neutral-700">No Phone Number on File</p>
                <p className="text-xs text-neutral-500 mt-1">
                  Add a phone number in your profile settings to enable SMS notifications
                </p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between p-3 rounded-soft border border-neutral-200 bg-neutral-50">
                  <div>
                    <p className="font-medium text-neutral-900">SMS Notifications</p>
                    <p className="text-xs text-neutral-600">
                      Send to: {phoneNumber}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleSmsEnabledToggle}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      smsPreferences.enabled
                        ? 'bg-primary-600'
                        : 'bg-neutral-300'
                    }`}
                  >
                    <span
                      className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                        smsPreferences.enabled ? 'translate-x-6' : ''
                      }`}
                    />
                  </button>
                </div>

                {smsPreferences.enabled && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Preferred Time for Daily SMS
                      </label>
                      <input
                        type="time"
                        value={smsPreferences.preferredTime}
                        onChange={(e) => handleSmsTimeChange(e.target.value)}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-soft focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      <p className="text-xs text-neutral-500 mt-1">
                        SMS reminder will be sent at this time in your timezone
                      </p>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-neutral-700 mb-3">Which SMS notifications to receive:</p>
                      <div className="space-y-2">
                        {[
                          { key: 'dailyReminder', label: 'Daily Check-in Reminder', icon: '📱' },
                          { key: 'clinicianMessages', label: 'Clinician Messages', icon: '💬' },
                          { key: 'milestones', label: 'Milestone Celebrations', icon: '🎉' },
                          { key: 'safetyAlerts', label: 'Safety Alerts', icon: '🚨' },
                          { key: 'lapseSupport', label: 'Setback Support', icon: '💪' },
                          { key: 'appointments', label: 'Appointment Reminders', icon: '📅' }
                        ].map(notif => (
                          <label key={notif.key} className="flex items-center p-2 rounded hover:bg-neutral-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={smsPreferences.types[notif.key] || false}
                              onChange={() => handleSmsToggle(notif.key)}
                              className="w-4 h-4"
                            />
                            <span className="ml-3 text-sm text-neutral-700">
                              {notif.icon} {notif.label}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={handleSendSMSTest}
                        disabled={smsTestSending}
                        className="flex-1"
                      >
                        {smsTestSending ? 'Sending...' : 'Send Test SMS'}
                      </Button>
                      <Button
                        type="button"
                        onClick={handleSaveSMSPreferences}
                        disabled={smsSaving}
                        className="flex-1"
                      >
                        {smsSaving ? 'Saving...' : 'Save SMS Settings'}
                      </Button>
                    </div>

                    <div className="p-3 rounded-soft bg-blue-50 border border-blue-200">
                      <p className="text-xs text-blue-800">
                        <strong>Standard messaging rates apply.</strong> You can disable SMS notifications at any time.
                      </p>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </Card>
      )}

      {/* Privacy Notice */}
      <Card className="border-l-4 border-l-info-500 bg-info-50">
        <div className="flex gap-3">
          <AlertCircle className="w-5 h-5 text-info-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-info-900">Privacy & Security</p>
            <p className="text-sm text-info-800 mt-1">
              Your email address and phone number are never shared with third parties. We only send wellness-focused notifications you've authorized. All SMS messages are sent through secure, HIPAA-compliant channels.
            </p>
          </div>
        </div>
      </Card>

      <Button
        type="button"
        onClick={handleSavePreferences}
        disabled={saving}
        className="w-full md:w-auto"
      >
        {saving ? 'Saving...' : 'Save Notification Settings'}
      </Button>
    </div>
  );
};

export default NotificationSettings;
